import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Container from './Container';
import { Turnstile } from '@marsidev/react-turnstile';

const SignupPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessName: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    city: '',
    address: '',
    streetNumber: '',
    moreInfo: '',
    mobile: '',
    lineNumber: '',
    acceptTerms: false
  });

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Refs for auto-focus
  const step1InputRef = useRef(null);
  const step2InputRef = useRef(null);
  const step3InputRef = useRef(null);
  const step4InputRef = useRef(null);
  const step5InputRef = useRef(null);

  // Auto-focus on step change
  useEffect(() => {
    const refs = {
      1: step1InputRef,
      2: step2InputRef,
      3: step3InputRef,
      4: step4InputRef,
      5: step5InputRef
    };
    const ref = refs[currentStep];
    if (ref?.current) {
      setTimeout(() => ref.current.focus(), 100);
    }
  }, [currentStep]);

  const steps = [
    { id: 1, name: 'Επιχείρηση', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg> },
    { id: 2, name: 'Στοιχεία', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg> },
    { id: 3, name: 'Κωδικός', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg> },
    { id: 4, name: 'Διεύθυνση', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
    { id: 5, name: 'Επικοινωνία', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg> }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const handleComplete = async () => {
    if (isLoading) {
      if (import.meta.env.DEV) console.log('Signup already in progress, ignoring duplicate request');
      return;
    }

    setIsLoading(true);
    setError('');

    if (!turnstileToken) {
      setError('Παρακαλώ περιμένετε την επαλήθευση ασφαλείας.');
      setIsLoading(false);
      return;
    }

    try {
      if (import.meta.env.DEV) console.log('Starting signup process...');
      const emailRedirectTo = `${window.location.origin}/login?email_confirmed=true`;
      
      const signUpOptions = {
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo,
          data: {
            business_name: formData.businessName,
            first_name: formData.firstName,
            last_name: formData.lastName,
            city: formData.city,
            address: formData.address,
            street_number: formData.streetNumber,
            address_more_info: formData.moreInfo,
            phone_line: formData.lineNumber,
            phone_mobile: formData.mobile
          }
        }
      };
      
      const { data: authData, error: authError } = await supabase.auth.signUp(signUpOptions);

      if (authError) throw new Error(authError.message);
      if (!authData.user) throw new Error('Failed to create user account');

      const maxRetries = 3;
      let signupData = null;
      let signupError = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        await new Promise(resolve => setTimeout(resolve, attempt * 500));

        const result = await supabase.rpc('handle_signup', {
          p_user_id: authData.user.id,
          p_business_name: formData.businessName,
          p_email: formData.email,
          p_first_name: formData.firstName,
          p_last_name: formData.lastName,
          p_city: formData.city,
          p_address: formData.address,
          p_street_number: formData.streetNumber,
          p_more_info: formData.moreInfo,
          p_mobile_phone: formData.mobile,
          p_line_phone: formData.lineNumber
        });

        signupData = result.data;
        signupError = result.error;

        if (!signupError || signupError.code !== '23503') break;
      }

      if (signupError) {
        throw new Error(
          'Ο λογαριασμός σας δημιουργήθηκε αλλά παρουσιάστηκε σφάλμα κατά τη ρύθμιση της κλινικής. ' +
          `(${signupError.message})`
        );
      }

      if (signupData && signupData.error) {
        throw new Error(
          'Ο λογαριασμός σας δημιουργήθηκε αλλά παρουσιάστηκε σφάλμα κατά τη ρύθμιση της κλινικής. ' +
          `(${signupData.error})`
        );
      }

      setTurnstileToken('');
      turnstileRef.current?.reset();
      navigate('/email-verification');

    } catch (err) {
      if (import.meta.env.DEV) console.error('Signup error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const phoneRegex = /^(\+30)?\d{10}$/;

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.businessName.trim().length > 0;
      case 2:
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return formData.email.trim().length > 0 &&
               emailRegex.test(formData.email) &&
               formData.firstName.trim().length > 0 &&
               formData.lastName.trim().length > 0;
      case 3:
        return formData.password.length >= 6 && formData.password === formData.confirmPassword;
      case 4:
        return formData.city.trim().length > 0 && formData.address.trim().length > 0 && formData.streetNumber.trim().length > 0;
      case 5:
        const isLineNumberValid = formData.lineNumber.trim().length > 0 && phoneRegex.test(formData.lineNumber.replace(/\s/g, ''));
        const isMobileValid = formData.mobile.trim().length === 0 || phoneRegex.test(formData.mobile.replace(/\s/g, ''));
        return isLineNumberValid && isMobileValid && formData.acceptTerms;
      default:
        return false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isStepValid()) {
        if (currentStep < 5) {
          handleNext();
        } else {
          handleComplete();
        }
      }
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { score: 1, label: 'Αδύναμος', color: '#ef4444', bars: 1 };
    if (score <= 4) return { score: 2, label: 'Μέτριος', color: '#f59e0b', bars: 2 };
    return { score: 3, label: 'Ισχυρός', color: '#10b981', bars: 3 };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const EyeIcon = ({ show, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors"
    >
      {show ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )}
    </button>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="animate-fade-in" onKeyDown={handleKeyDown}>
            <h2 className="text-2xl font-bold text-white mb-2">Πώς λέγεται η κτηνιατρική σας κλινική;</h2>
            <p className="text-gray-400 mb-8">Εισάγετε το όνομα της επιχείρησής σας για να ξεκινήσουμε.</p>
            
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <input
                ref={step1InputRef}
                type="text"
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                placeholder="π.χ. Κτηνιατρική Κλινική Αθηνάς"
                maxLength={100}
                className="w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>
        );

      case 2:
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const showEmailError = formData.email.trim().length > 0 && !emailRegex.test(formData.email);

        return (
          <div className="animate-fade-in" onKeyDown={handleKeyDown}>
            <h2 className="text-2xl font-bold text-white mb-2">Ποια είναι τα προσωπικά σας στοιχεία;</h2>
            <p className="text-gray-400 mb-8">Θα τα χρειαστούμε για τη δημιουργία του λογαριασμού σας.</p>

            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    ref={step2InputRef}
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Όνομα *"
                    maxLength={50}
                    className="w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                  />
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Επώνυμο *"
                    maxLength={50}
                    className="w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Email *"
                    className={`w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all ${
                      showEmailError 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/50' 
                        : 'border-gray-600 focus:border-emerald-500 focus:ring-emerald-500/50'
                    }`}
                  />
                </div>
                {showEmailError && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    Παρακαλώ εισάγετε ένα έγκυρο email
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="animate-fade-in" onKeyDown={handleKeyDown}>
            <h2 className="text-2xl font-bold text-white mb-2">Δημιουργήστε έναν ασφαλή κωδικό</h2>
            <p className="text-gray-400 mb-8">Εισάγετε τον κωδικό σας δύο φορές για επιβεβαίωση.</p>
            
            <div className="space-y-5">
              {/* Password */}
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    ref={step3InputRef}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Κωδικός πρόσβασης *"
                    className="w-full pl-11 pr-12 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                  />
                  <EyeIcon show={showPassword} onClick={() => setShowPassword(!showPassword)} />
                </div>

                {/* Strength Meter */}
                {formData.password && (
                  <div className="mt-3 px-1">
                    <div className="flex gap-2 mb-1.5">
                      {[1, 2, 3].map((level) => (
                        <div
                          key={level}
                          className="h-1.5 flex-1 rounded-full transition-colors duration-300"
                          style={{
                            backgroundColor: passwordStrength.bars >= level ? passwordStrength.color : '#374151'
                          }}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-medium" style={{ color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Επιβεβαίωση κωδικού *"
                    className="w-full pl-11 pr-12 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                  />
                  <EyeIcon show={showConfirmPassword} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
                </div>
                {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    Οι κωδικοί δεν ταιριάζουν
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="animate-fade-in" onKeyDown={handleKeyDown}>
            <h2 className="text-2xl font-bold text-white mb-2">Πού βρίσκεται η κλινική σας;</h2>
            <p className="text-gray-400 mb-8">Εισάγετε την πόλη και τη διεύθυνση της κλινικής σας.</p>
            
            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                </div>
                <input
                  ref={step4InputRef}
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Πόλη *"
                  maxLength={100}
                  className="w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Οδός *"
                    maxLength={200}
                    className="w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                  />
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.streetNumber}
                    onChange={(e) => handleInputChange('streetNumber', e.target.value)}
                    placeholder="Αριθμός *"
                    maxLength={20}
                    className="w-full px-4 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-start pt-3.5 pointer-events-none text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <textarea
                  value={formData.moreInfo}
                  onChange={(e) => handleInputChange('moreInfo', e.target.value)}
                  placeholder="Περισσότερες πληροφορίες (π.χ. Όροφος, Κουδούνι)"
                  maxLength={200}
                  rows={2}
                  className="w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all resize-none"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        const showLineNumberError = formData.lineNumber.trim().length > 0 && !phoneRegex.test(formData.lineNumber.replace(/\s/g, ''));
        const showMobileError = formData.mobile.trim().length > 0 && !phoneRegex.test(formData.mobile.replace(/\s/g, ''));

        return (
          <div className="animate-fade-in" onKeyDown={handleKeyDown}>
            <h2 className="text-2xl font-bold text-white mb-2">Πώς μπορούμε να επικοινωνήσουμε μαζί σας;</h2>
            <p className="text-gray-400 mb-8">Εισάγετε τα στοιχεία επικοινωνίας της κλινικής σας.</p>
            
            <div className="space-y-5">
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  </div>
                  <input
                    ref={step5InputRef}
                    type="tel"
                    value={formData.lineNumber}
                    onChange={(e) => handleInputChange('lineNumber', e.target.value)}
                    placeholder="Σταθερό τηλέφωνο * (π.χ. 2101234567)"
                    maxLength={15}
                    className={`w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all ${
                      showLineNumberError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/50' : 'border-gray-600 focus:border-emerald-500 focus:ring-emerald-500/50'
                    }`}
                  />
                </div>
                {showLineNumberError && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    Παρακαλώ εισάγετε 10 ψηφία (με ή χωρίς +30)
                  </p>
                )}
              </div>

              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  </div>
                  <input
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    placeholder="Κινητό (προαιρετικό)"
                    maxLength={15}
                    className={`w-full pl-11 pr-4 py-3 bg-[#1F2937]/50 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all ${
                      showMobileError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/50' : 'border-gray-600 focus:border-emerald-500 focus:ring-emerald-500/50'
                    }`}
                  />
                </div>
                {showMobileError && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    Παρακαλώ εισάγετε 10 ψηφία (με ή χωρίς +30)
                  </p>
                )}
              </div>

              {/* Terms and Conditions Checkbox */}
              <label className="flex items-start gap-3 mt-6 p-4 bg-[#1F2937]/50 border border-gray-700 rounded-xl cursor-pointer group hover:bg-[#1F2937] transition-colors">
                <div className="relative flex items-center justify-center mt-0.5">
                  <input
                    type="checkbox"
                    checked={formData.acceptTerms}
                    onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                    className="peer appearance-none w-5 h-5 border border-gray-500 rounded bg-[#111827] checked:bg-emerald-500 checked:border-emerald-500 transition-all cursor-pointer"
                  />
                  <svg className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="text-sm text-gray-300 leading-relaxed">
                  Αποδέχομαι τους{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 font-medium underline underline-offset-2 transition-colors" onClick={(e) => e.stopPropagation()}>
                    Όρους Χρήσης
                  </a>
                  {' '}και την{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 font-medium underline underline-offset-2 transition-colors" onClick={(e) => e.stopPropagation()}>
                    Πολιτική Απορρήτου
                  </a>
                  {' '}της πλατφόρμας.
                </div>
              </label>

              <div className="pt-2">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                  onSuccess={setTurnstileToken}
                  onError={() => setTurnstileToken('')}
                  onExpire={() => setTurnstileToken('')}
                  theme="dark"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#1A202C] relative overflow-hidden py-10 lg:py-16">
      {/* Background ambient glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      
      <div className="w-full max-w-2xl mx-auto px-4 relative z-10">
        
        {/* Progress Stepper */}
        <div className="mb-10">
          <div className="sm:hidden text-center text-white text-sm font-medium mb-4">
            Βήμα {currentStep} από 5
          </div>
          
          <div className="flex items-center justify-center max-w-lg mx-auto">
            {steps.map((step, index) => {
              const isCompleted = currentStep > step.id;
              const isCurrent = currentStep === step.id;
              const isPending = currentStep < step.id;
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center relative group">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted || isCurrent 
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30' 
                          : 'bg-gray-800 text-gray-500 border border-gray-700'
                      }`}
                    >
                      {isCompleted ? (
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <span className={isCurrent ? 'text-white' : 'text-gray-500'}>{step.icon}</span>
                      )}
                    </div>
                    <span className={`hidden sm:block absolute -bottom-6 w-max text-xs font-bold uppercase tracking-wider transition-colors ${
                      isCurrent ? 'text-emerald-400' : isCompleted ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {step.name}
                    </span>
                  </div>
                  
                  {index < steps.length - 1 && (
                    <div className="flex-1 px-2 sm:px-4">
                      <div 
                        className={`h-[2px] w-full rounded-full transition-all duration-300 ${
                          isCompleted ? 'bg-emerald-500' : 'bg-gray-700'
                        }`}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Glassmorphic Form Card */}
        <div className="bg-[#111827]/80 backdrop-blur-xl border border-gray-700/50 rounded-3xl p-6 sm:p-10 shadow-2xl mt-8 sm:mt-12">
          
          <div className="min-h-[300px]">
            {renderStepContent()}
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium flex items-start gap-3 shadow-lg">
              <div className="p-1 bg-red-500/20 rounded-md flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="leading-snug">{error}</span>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex flex-col-reverse sm:flex-row justify-between items-center mt-10 gap-4 pt-6 border-t border-gray-800">
            {currentStep > 1 ? (
              <button
                onClick={handlePrevious}
                disabled={isLoading}
                className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold text-gray-300 border border-gray-600 hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                Πίσω
              </button>
            ) : (
              <div className="hidden sm:block w-auto px-8"></div> // Placeholder for flex alignment
            )}

            {currentStep < 5 ? (
              <button
                onClick={handleNext}
                disabled={!isStepValid() || isLoading}
                className="w-full sm:w-auto px-10 py-3 rounded-xl font-bold text-white transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={isStepValid() && !isLoading ? { background: 'linear-gradient(135deg, #10B981, #059669)' } : { backgroundColor: '#374151' }}
              >
                Επόμενο
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={!isStepValid() || isLoading || !turnstileToken}
                className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold text-white transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={(isStepValid() && !isLoading && turnstileToken) ? { background: 'linear-gradient(135deg, #10B981, #059669)' } : { backgroundColor: '#374151' }}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Δημιουργία...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                    Ολοκλήρωση
                  </>
                )}
              </button>
            )}
          </div>
          
          {currentStep === 1 && (
            <div className="text-center mt-6">
              <p className="text-gray-400 text-sm">
                Έχετε ήδη λογαριασμό;{' '}
                <Link to="/login" className="font-semibold text-emerald-400 hover:text-emerald-300 transition-colors underline underline-offset-2">
                  Συνδεθείτε εδώ
                </Link>
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SignupPage;